import TableName from "../../../components/common/tableName";
import Button from "../../../../common/components/button";
import Input from "../../../components/common/input";
import { useEffect, useState } from "react";

import { useGetMemberInfo } from "../../../../common/hooks/console/member/useGetMemberInfo";
import { useDeleteMember } from "../../../../common/hooks/console/member/useDeleteMember";
import useToastStore from "../../../store/useToastStore";
import { useSystemPopStore } from "../../../store/useSystemPopStore";
import { usePutMember } from "../../../../common/hooks/console/member/usePutMember";
import { useGetCommonCustomer } from "../../../../common/hooks/common/useGetCommonCustomer";
import SelectBox from "../../../components/common/selectBox";
import { useAuthStore } from "../../../store/useAuthStore";

const ModifyMember = ({
    memberIdx,
    onComplete,
    onRefresh,
}: {
    memberIdx: number;
    onComplete: (id: number) => void;
    onRefresh?: (refetchFn: () => Promise<any>) => void;
}) => {
    const level = useAuthStore(state => state.m_level);

    const { addToast } = useToastStore();
    const { showPop } = useSystemPopStore();

    const [memberType, setMemberType] = useState<string | null>(null);
    const [customerName, setCustomerName] = useState<string>("");
    const [memberName, setMemberName] = useState<string>("");
    const [mobile, setMobile] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [memo, setMemo] = useState<string>("");
    const [searchtxt, setSearchtxt] = useState<string>("");
    const [customerIdx, setCustomerIdx] = useState<number>(0);
    const [customerNameError, setCustomerNameError] = useState<boolean>(false);
    const [memberNameError, setMemberNameError] = useState<boolean>(false);
    const [mobileError, setMobileError] = useState<boolean>(false);
    const [emailError, setEmailError] = useState<boolean>(false);

    const { data: customerData } = useGetCommonCustomer(memberType === "public" ? 2 : 1, searchtxt);
    const customerOptions =
        customerData?.data.map(customer => ({
            value: customer.customer_idx.toString(),
            label: customer.customer_name,
        })) || [];

    const { data, refetch } = useGetMemberInfo(memberIdx);
    const deleteMemberMutation = useDeleteMember();
    const putMemberMutation = usePutMember(
        () =>
            addToast({
                type: "success",
                message: "회원정보가 변경되었습니다.",
            }),
        memberIdx.toString(),
    );

    const handleDelete = () => {
        // console.log("삭제하려는 memberIdx:", memberIdx);

        showPop({
            title: "해당 내역을 삭제하시겠습니까",
            rightButton: "확인",
            type: "negative",
            onRightClick: () => {
                addToast({
                    type: "delete",
                    onConfirmDelete: () => {
                        deleteMemberMutation.mutate(
                            { id: memberIdx },
                            {
                                onSuccess: () => {
                                    // console.log("삭제 완료된 memberIdx:", memberIdx);

                                    onComplete(memberIdx);
                                },
                            },
                        );
                    },
                });
            },
        });
    };

    const handleSubmit = () => {
        let hasError = false;
        if (!customerName.trim()) {
            setCustomerNameError(true);
            hasError = true;
        } else {
            setCustomerNameError(false);
        }
        if (!memberName.trim()) {
            setMemberNameError(true);
            hasError = true;
        } else {
            setMemberNameError(false);
        }
        if (!mobile.trim()) {
            setMobileError(true);
            hasError = true;
        } else {
            setMobileError(false);
        }
        if (!email.trim()) {
            setEmailError(true);
            hasError = true;
        } else {
            setEmailError(false);
        }
        if (hasError) return;

        putMemberMutation.mutate(
            {
                m_name: memberName,
                m_mobile: mobile,
                m_email: email,
                m_memo: memo,
                m_password: password,
                customer_idx: customerIdx ?? null,
                customer_name: memberType === "private" ? customerName : undefined,
            },
            {
                onError: (error: any) => {
                    const errorMessage = error?.response?.data?.message || "알 수 없는 오류가 발생했습니다.";
                    showPop({
                        title: errorMessage,
                        rightButton: undefined,
                        type: "negative",
                        onRightClick: () => {},
                    });
                },
            },
        );
    };
    useEffect(() => {
        if (data) {
            setMemberType(data.m_category === 1 ? "public" : "private");
            setCustomerIdx(data.customer_idx || 0);
            setCustomerName(data.customer_name || "");
            setMemberName(data.m_name || "");
            setMobile(data.m_mobile || "");
            setEmail(data.m_email || "");
            setMemo(data.m_memo || "");
        }
    }, [data]);

    useEffect(() => {
        const selectedCustomer = customerOptions.find(option => option.value === customerIdx.toString());
        if (selectedCustomer) {
            setCustomerName(selectedCustomer.label);
        }
    }, [customerOptions, customerIdx]);

    useEffect(() => {
        if (onRefresh) {
            onRefresh(refetch);
        }
    }, [onRefresh]);

    return (
        <div className="bg-white shadow-custom rounded-xl px-3 py-5 flex flex-col gap-5">
            <div className="flex justify-between items-center">
                <p className="text-xl font-bold ">회원정보 변경</p>
                {level !== 7 && level !== 8 && (
                    <Button onClick={handleDelete} size="S" status="error" className="px-2">
                        회원정보 삭제
                    </Button>
                )}
            </div>
            <div className="border-t-2 border-gray2">
                <div className="border-b border-gray4 w-full flex items-center">
                    <TableName className="h-12">가입일자</TableName>
                    <p className="text-sm pl-2">{data?.reg_date.replaceAll("-", ".")}</p>
                </div>
                <div className="flex w-full">
                    <div className="border-b border-gray4 w-full flex items-center">
                        <TableName className="h-12">주문/완료</TableName>
                        <p className="text-sm pl-2">
                            {data?.order_count} 건 / {data?.delivery_completed_count} 건
                        </p>
                    </div>
                    <div className="border-b border-gray4 w-full flex items-center">
                        <TableName className="h-12">누적판매금액</TableName>
                        <p className="text-sm pl-2">{data?.total_sales_amount.toLocaleString()}원</p>
                    </div>
                </div>
                <div className="border-b border-gray4 w-full flex items-center">
                    <TableName className="h-12">회원구분</TableName>
                    <p className="text-sm pl-2">{data?.m_category_text}</p>
                </div>
                <div className="flex w-full">
                    <div className="border-b border-gray4 w-full flex items-center">
                        <TableName className="h-12" required={true}>
                            고객명
                        </TableName>
                        {/* {memberType === "public" ? ( */}
                        <SelectBox
                            disabled={level === 7}
                            height="max-h-[400px]"
                            length={10}
                            onSearchChange={setSearchtxt}
                            searchable={true}
                            className="w-full px-2"
                            label={customerName}
                            options={customerOptions}
                            value={customerIdx.toString()}
                            onChange={value => {
                                const selectedCustomer = customerOptions.find(option => option.value === value);
                                setCustomerIdx(Number(value));
                                setCustomerName(selectedCustomer?.label || "");
                            }}
                        />
                        {/* ) : (
                            <Input
                                className="px-2"
                                placeholder="고객명 입력"
                                value={customerName}
                                onChange={e => {
                                    const inputText = e.target.value;
                                    // if (inputText.length <= 30) {
                                    // setCustomerNameError(false);
                                    setCustomerName(inputText);
                                    // }
                                }}
                                status={customerNameError ? "error" : level === 7 ? "view" : undefined}
                            />
                        )} */}
                    </div>
                    <div className="border-b border-gray4 w-full flex items-center">
                        <TableName className="h-12" required={true}>
                            회원명
                        </TableName>
                        <Input
                            status={memberNameError ? "error" : level === 7 ? "view" : undefined}
                            className="px-2"
                            placeholder="20자"
                            value={memberName}
                            onChange={e => {
                                const inputText = e.target.value;
                                if (inputText.length <= 20) {
                                    setMemberNameError(false);
                                    setMemberName(inputText);
                                }
                            }}
                        />
                    </div>
                </div>

                <div className="flex w-full">
                    <div className="border-b border-gray4 w-full flex items-center">
                        <TableName className="h-12" required={true}>
                            연락처
                        </TableName>
                        <Input
                            status={mobileError ? "error" : level === 7 ? "view" : undefined}
                            className="px-2"
                            value={mobile}
                            onChange={e => {
                                setMobileError(false);
                                const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
                                let formattedNumber = "";

                                if (onlyNumbers.length <= 3) {
                                    formattedNumber = onlyNumbers;
                                } else if (onlyNumbers.length <= 7) {
                                    formattedNumber = `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3)}`;
                                } else {
                                    formattedNumber = `${onlyNumbers.slice(0, 3)}-${onlyNumbers.slice(3, 7)}-${onlyNumbers.slice(7, 11)}`;
                                }

                                setMobile(formattedNumber);
                            }}
                        />
                    </div>
                    <div className="border-b border-gray4 w-full flex items-center">
                        <TableName className="h-12" required={true}>
                            이메일
                        </TableName>
                        <Input
                            status={emailError ? "error" : level === 7 ? "view" : undefined}
                            className="px-2"
                            value={email}
                            onChange={e => {
                                setEmailError(false);
                                setEmail(e.target.value);
                            }}
                        />
                    </div>
                </div>
                <div className="flex items-center w-1/2">
                    <TableName className="h-12">비밀번호</TableName>
                    <Input
                        status={level === 7 ? "view" : undefined}
                        className="px-2"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                    />
                </div>
                <div className="border-y border-gray4 flex items-center">
                    <TableName className="h-[120px]" required={false}>
                        메모 입력
                    </TableName>
                    <textarea
                        disabled={level === 7}
                        className="p-2 m-2 border outline-0 h-[100px] w-full rounded-lg border-gray4 text-sm resize-none"
                        value={memo}
                        onChange={e => setMemo(e.target.value)}
                    />
                </div>
            </div>
            {level !== 7 && (
                <div className="flex w-full gap-2">
                    <Button size="L" className="w-full">
                        취소
                    </Button>
                    <Button size="L" status="regist" className="w-full" onClick={handleSubmit}>
                        저장
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ModifyMember;
